import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Badge } from './badge';
import { Card, CardContent } from './card';
import { Plus, X, Trash2, Filter, Play, Save, RotateCcw } from 'lucide-react';

// Application fields schema
const APPLICATION_FIELDS = [
  { id: 'student_name', label: 'Student Name', type: 'text' },
  { id: 'student_email', label: 'Email', type: 'text' },
  { id: 'student_phone', label: 'Phone', type: 'text' },
  { id: 'country', label: 'Country', type: 'select', dynamic: 'countries' },
  { id: 'status', label: 'Status', type: 'select', dynamic: 'statuses' },
  { id: 'current_desk', label: 'Current Desk', type: 'select', dynamic: 'desks' },
  { id: 'assigned_to', label: 'Assigned To', type: 'select', dynamic: 'users' },
  { id: 'university_id', label: 'University', type: 'select', dynamic: 'universities' },
  { id: 'program_id', label: 'Program', type: 'select', dynamic: 'programs' },
  { id: 'created_at', label: 'Created Date', type: 'date' },
  { id: 'updated_at', label: 'Updated Date', type: 'date' },
  { id: 'is_agent_submission', label: 'Agent Submission', type: 'boolean' },
];

// Operators by field type
const OPERATORS = {
  text: [
    { id: 'eq', label: 'Equals' },
    { id: 'ne', label: 'Not Equals' },
    { id: 'contains', label: 'Contains' },
    { id: 'starts_with', label: 'Starts With' },
    { id: 'ends_with', label: 'Ends With' },
    { id: 'is_empty', label: 'Is Empty' },
    { id: 'is_not_empty', label: 'Is Not Empty' },
  ],
  select: [
    { id: 'eq', label: 'Equals' },
    { id: 'ne', label: 'Not Equals' },
    { id: 'in', label: 'In List' },
    { id: 'not_in', label: 'Not In List' },
  ],
  date: [
    { id: 'eq', label: 'Equals' },
    { id: 'ne', label: 'Not Equals' },
    { id: 'gt', label: 'After' },
    { id: 'gte', label: 'On or After' },
    { id: 'lt', label: 'Before' },
    { id: 'lte', label: 'On or Before' },
    { id: 'between', label: 'Between' },
  ],
  boolean: [
    { id: 'eq', label: 'Equals' },
  ],
};

// Single condition component
const ConditionRow = ({ 
  condition, 
  index, 
  onUpdate, 
  onRemove, 
  dynamicOptions,
  isFirst 
}) => {
  const field = APPLICATION_FIELDS.find(f => f.id === condition.field);
  const operators = OPERATORS[field?.type || 'text'] || OPERATORS.text;
  const needsValue = !['is_empty', 'is_not_empty'].includes(condition.operator);
  
  // Get options for select fields
  const getSelectOptions = () => {
    if (!field?.dynamic) return [];
    return dynamicOptions[field.dynamic] || [];
  };

  const selectOptions = getSelectOptions();
  
  // Pre-render select items
  const fieldSelectItems = [];
  for (let i = 0; i < APPLICATION_FIELDS.length; i++) {
    const f = APPLICATION_FIELDS[i];
    fieldSelectItems.push(
      <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
    );
  }
  
  const operatorSelectItems = [];
  for (let i = 0; i < operators.length; i++) {
    const op = operators[i];
    operatorSelectItems.push(
      <SelectItem key={op.id} value={op.id}>{op.label}</SelectItem>
    );
  }
  
  const valueSelectItems = [];
  for (let i = 0; i < selectOptions.length; i++) {
    const opt = selectOptions[i];
    valueSelectItems.push(
      <SelectItem key={opt.id || opt.value || opt} value={opt.id || opt.value || opt}>
        {opt.name || opt.label || opt}
      </SelectItem>
    );
  }

  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 border border-border">
      {!isFirst && (
        <Badge variant="outline" className="shrink-0 text-xs">
          {condition.logic || 'AND'}
        </Badge>
      )}
      
      {/* Field Select */}
      <Select 
        value={condition.field} 
        onValueChange={(val) => onUpdate(index, { ...condition, field: val, value: '' })}
      >
        <SelectTrigger className="w-[150px] h-8">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent>
          {fieldSelectItems}
        </SelectContent>
      </Select>

      {/* Operator Select */}
      <Select 
        value={condition.operator} 
        onValueChange={(val) => onUpdate(index, { ...condition, operator: val })}
      >
        <SelectTrigger className="w-[130px] h-8">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {operatorSelectItems}
        </SelectContent>
      </Select>

      {/* Value Input */}
      {needsValue && (
        <>
          {field?.type === 'select' ? (
            <Select 
              value={condition.value} 
              onValueChange={(val) => onUpdate(index, { ...condition, value: val })}
            >
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue placeholder="Select value" />
              </SelectTrigger>
              <SelectContent>
                {valueSelectItems}
              </SelectContent>
            </Select>
          ) : field?.type === 'date' ? (
            <Input
              type="date"
              value={condition.value}
              onChange={(e) => onUpdate(index, { ...condition, value: e.target.value })}
              className="w-[150px] h-8"
            />
          ) : field?.type === 'boolean' ? (
            <Select 
              value={condition.value} 
              onValueChange={(val) => onUpdate(index, { ...condition, value: val })}
            >
              <SelectTrigger className="w-[100px] h-8">
                <SelectValue placeholder="Value" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={condition.value}
              onChange={(e) => onUpdate(index, { ...condition, value: e.target.value })}
              placeholder="Enter value..."
              className="w-[150px] h-8"
            />
          )}
        </>
      )}

      {/* Remove Button */}
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-8 w-8 shrink-0"
        onClick={() => onRemove(index)}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Condition Group component (for nested AND/OR)
const ConditionGroup = ({ 
  group, 
  groupIndex, 
  onUpdate, 
  onRemove, 
  dynamicOptions,
  level = 0 
}) => {
  const addCondition = () => {
    const newConditions = [...group.conditions, { 
      field: 'student_name', 
      operator: 'contains', 
      value: '',
      logic: group.logic 
    }];
    onUpdate(groupIndex, { ...group, conditions: newConditions });
  };

  const updateCondition = (condIndex, newCondition) => {
    const newConditions = [...group.conditions];
    newConditions[condIndex] = newCondition;
    onUpdate(groupIndex, { ...group, conditions: newConditions });
  };

  const removeCondition = (condIndex) => {
    const newConditions = group.conditions.filter((_, i) => i !== condIndex);
    if (newConditions.length === 0) {
      onRemove(groupIndex);
    } else {
      onUpdate(groupIndex, { ...group, conditions: newConditions });
    }
  };

  const toggleLogic = () => {
    const newLogic = group.logic === 'AND' ? 'OR' : 'AND';
    const newConditions = group.conditions.map(c => ({ ...c, logic: newLogic }));
    onUpdate(groupIndex, { ...group, logic: newLogic, conditions: newConditions });
  };

  // Pre-render condition rows
  const conditionRows = [];
  for (let i = 0; i < group.conditions.length; i++) {
    const condition = group.conditions[i];
    conditionRows.push(
      <ConditionRow
        key={i}
        condition={condition}
        index={i}
        onUpdate={updateCondition}
        onRemove={removeCondition}
        dynamicOptions={dynamicOptions}
        isFirst={i === 0}
      />
    );
  }

  return (
    <div className={`space-y-2 ${level > 0 ? 'ml-4 pl-4 border-l-2 border-primary/30' : ''}`}>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLogic}
          className={`text-xs font-medium ${group.logic === 'OR' ? 'bg-orange-500/10 border-orange-500/50 text-orange-600' : 'bg-blue-500/10 border-blue-500/50 text-blue-600'}`}
        >
          {group.logic}
        </Button>
        <span className="text-xs text-muted-foreground">
          {group.logic === 'AND' ? 'All conditions must match' : 'Any condition can match'}
        </span>
      </div>
      
      <div className="space-y-2">
        {conditionRows}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={addCondition}
        className="text-xs"
      >
        <Plus className="w-3 h-3 mr-1" />
        Add Condition
      </Button>
    </div>
  );
};

// Main Query Builder Component
export const QueryBuilder = ({ 
  onApply, 
  onSave,
  savedFilters = [],
  onLoadFilter,
  onDeleteFilter,
  dynamicOptions = {} 
}) => {
  const [groups, setGroups] = useState([
    { logic: 'AND', conditions: [] }
  ]);
  const [globalLogic, setGlobalLogic] = useState('AND');
  const [filterName, setFilterName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const addGroup = () => {
    setGroups([...groups, { logic: 'AND', conditions: [{ field: 'student_name', operator: 'contains', value: '', logic: 'AND' }] }]);
  };

  const updateGroup = (index, newGroup) => {
    const newGroups = [...groups];
    newGroups[index] = newGroup;
    setGroups(newGroups);
  };

  const removeGroup = (index) => {
    setGroups(groups.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    // Build the query object
    const query = {
      logic: globalLogic,
      groups: groups.filter(g => g.conditions.length > 0)
    };
    onApply(query);
  };

  const handleSave = () => {
    if (!filterName.trim()) return;
    onSave({
      name: filterName,
      query: {
        logic: globalLogic,
        groups: groups.filter(g => g.conditions.length > 0)
      }
    });
    setFilterName('');
    setShowSaveDialog(false);
  };

  const handleReset = () => {
    setGroups([{ logic: 'AND', conditions: [] }]);
    setGlobalLogic('AND');
    onApply(null);
  };

  const loadFilter = (filter) => {
    if (filter.query) {
      setGroups(filter.query.groups || [{ logic: 'AND', conditions: [] }]);
      setGlobalLogic(filter.query.logic || 'AND');
    }
    if (onLoadFilter) onLoadFilter(filter);
  };

  const activeConditionsCount = groups.reduce((sum, g) => sum + g.conditions.length, 0);

  // Pre-render saved filter items
  const savedFilterItems = [];
  for (let i = 0; i < savedFilters.length; i++) {
    const filter = savedFilters[i];
    savedFilterItems.push(
      <div 
        key={filter.id || i}
        className="flex items-center justify-between p-2 rounded-lg border border-border hover:bg-muted/50 cursor-pointer"
        onClick={() => loadFilter(filter)}
      >
        <span className="text-sm">{filter.name}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={(e) => { e.stopPropagation(); onDeleteFilter(filter.id); }}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  // Pre-render condition groups
  const groupElements = [];
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (i > 0) {
      groupElements.push(
        <div key={`logic-${i}`} className="flex items-center gap-2 py-2">
          <div className="flex-1 h-px bg-border" />
          <Badge 
            variant="outline" 
            className={`cursor-pointer ${globalLogic === 'OR' ? 'bg-orange-500/10 border-orange-500/50 text-orange-600' : 'bg-blue-500/10 border-blue-500/50 text-blue-600'}`}
            onClick={() => setGlobalLogic(globalLogic === 'AND' ? 'OR' : 'AND')}
          >
            {globalLogic}
          </Badge>
          <div className="flex-1 h-px bg-border" />
        </div>
      );
    }
    groupElements.push(
      <ConditionGroup
        key={i}
        group={group}
        groupIndex={i}
        onUpdate={updateGroup}
        onRemove={removeGroup}
        dynamicOptions={dynamicOptions}
      />
    );
  }

  return (
    <Card className="border-2 border-dashed border-primary/30">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-primary" />
            <span className="font-medium">Advanced Query Builder</span>
            {activeConditionsCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeConditionsCount} condition{activeConditionsCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {savedFilters.length > 0 && (
              <Select onValueChange={(val) => loadFilter(savedFilters.find(f => f.id === val))}>
                <SelectTrigger className="w-[150px] h-8">
                  <SelectValue placeholder="Load saved..." />
                </SelectTrigger>
                <SelectContent>
                  {savedFilterItems}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Condition Groups */}
        <div className="space-y-2">
          {groupElements}
        </div>

        {/* Add Group Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={addGroup}
          className="text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Condition Group
        </Button>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowSaveDialog(true)}
              disabled={activeConditionsCount === 0}
            >
              <Save className="w-4 h-4 mr-1" />
              Save Filter
            </Button>
          </div>
          <Button 
            onClick={handleApply}
            className="bg-gradient-to-r from-amber-500 to-amber-500"
          >
            <Play className="w-4 h-4 mr-1" />
            Apply Filter
          </Button>
        </div>

        {/* Save Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background p-6 rounded-lg shadow-lg w-[400px] space-y-4">
              <h3 className="font-medium">Save Filter</h3>
              <Input
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Enter filter name..."
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QueryBuilder;
